import { Button, Container, Group, Paper, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { X } from 'tabler-icons-react';
import { OpenComponent } from '../../enums/misc.enum';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import useDidMountEffect from '../../hooks/useDidMountEffect';
import { IPaginationMeta } from '../../type/admin.types';
import { ErrorResponse } from '../../type/error-response.types';
import useAuth from '../../hooks/useAuth';
import {
  UserManagementFilterData,
  UserManagementFilterForm
} from '../../type/filter.types';
import {
  BasicAccount,
  RetrieveAccountsRequest
} from '../../type/retrieve-account.types';
import AdminCreateUser from '../admin-user-management/admin-create-user.component';
import AdminUpdateUser from '../admin-user-management/admin-update-user.component';
import AdminUserInformation from '../admin-user-management/admin-user-information.component';
import UserManagementTable from '../admin-user-management/components/admin-user-management-table.component';
import UpdateEnableDisableButtons from '../admin-user-management/components/update-enable-disable.button';
import SearchButton from '../buttons/search-button/search-button.component';
import { Roles } from '../../enums/roles-enum';

import PartnerUserFilter from '../filters/partner-user-filter.component';
import ResellerUserFilter from '../filters/reseller-user-filter.component';
import MerchantUserManagementFilter from '../filters/merchant-user-management-filter.component';
import UserManagementFilter from '../filters/admin-user-management-filter.component';
import { AccountTypes } from '../../enums/account-type';

const DynamicCreateButton = (
  setComponentManager: React.Dispatch<React.SetStateAction<OpenComponent>>,
  isLoading: boolean,
  currentAccountType: string | undefined
) => {
  let text = '';

  if (currentAccountType === AccountTypes.PARTNER) {
    text = 'Create Partner User';
  }
  if (currentAccountType === AccountTypes.RESELLER) {
    text = 'Create Reseller User';
  }
  if (currentAccountType === AccountTypes.MERCHANT) {
    text = 'Create Merchant User';
  }
  if (currentAccountType === AccountTypes.MERCHANT_BRANCH) {
    text = 'Create Branch User';
  }

  if (text) {
    return (
      <Button
        onClick={() => {
          setComponentManager(OpenComponent.CREATE_USER);
        }}
        disabled={isLoading}
      >
        {text}
      </Button>
    );
  } else {
    return;
  }
};

//sonarcloud

function AdminUserManagement() {
  const [search, setSearch] = useState('');

  const [isLoadResellers, setIsLoadResellers] = useState<boolean>(true);
  const [isLoadMerchants, setIsLoadMerchants] = useState<boolean>(true);
  const [isLoadPartners, setIsLoadPartners] = useState<boolean>(true);

  const [componentManager, setComponentManager] = useState(OpenComponent.HOME);
  const [paginationData, setPaginationData] = useState<IPaginationMeta>();

  const [firstUserId, setFirstUserId] = useState<number | undefined>();
  const [userData, setUserData] = useState<BasicAccount[]>();
  const [isChecked, setIsChecked] = useState<number[]>([]);
  const [checkedAll, setCheckedAll] = useState<boolean>(false);
  const [refreshTable, setRefreshTable] = useState(false);
  const [submitApplied, setSubmitApplied] = useState(false);
  const [clickedUserId, setClickedUserId] = useState<number>();
  const axiosPrivate = useAxiosPrivate();
  const [isLoading, setIsLoading] = useState(false);
  const { auth } = useAuth();

  const filterSelectData = useForm<UserManagementFilterData>({
    initialValues: {
      partnersData: [],
      resellersData: [],
      merchantsData: [],
      merchantBranchesData: []
    }
  });

  const filterValues = useForm<UserManagementFilterForm>({
    initialValues: {
      partner: '',
      reseller: '',
      merchant: '',
      merchantBranch: ''
    }
  });

  useEffect(() => {
    if (auth?.role !== Roles.ADMINISTRATOR) createRequest(1, true);
  }, []);

  const createRequest = async (
    page: number,
    clearIsChecked: boolean,
    username?: string
  ) => {
    const request: RetrieveAccountsRequest = {
      page: page || 1,
      filters: {
        limit: 20,
        partnerId: parseInt(filterValues.values.partner),
        resellerId: parseInt(filterValues.values.reseller),
        merchantId: parseInt(filterValues.values.merchant),
        branchId: parseInt(filterValues.values.merchantBranch),
        username: username
      }
    };

    const filters = request.filters;

    if (!filters.resellerId) {
      delete request.filters.resellerId;
    }
    if (!filters.merchantId) {
      delete request.filters.merchantId;
    }
    if (!filters.branchId) {
      delete request.filters.branchId;
    }
    if (!username) {
      delete request.filters.username;
    }

    try {
      setIsLoading(true);
      const response = await axiosPrivate.post('/web/accounts', request);
      const users: BasicAccount[] = response.data.items;
      const metadata: IPaginationMeta = response.data.metadata;

      setPaginationData(metadata);
      setUserData(users);
      setFirstUserId(response.data.firstUserId);

      if (clearIsChecked) {
        setIsChecked([]);
        setCheckedAll(false);
      }
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      showNotification({
        id: 'failed-to-submit-filter',
        message:
          error?.error ||
          err?.response?.data?.error ||
          'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000
      });
    } finally {
      setIsLoading(false);
    }
  };

  useDidMountEffect(() => {
    if (paginationData?.currentPage) {
      createRequest(paginationData.currentPage, false);
    }
  }, [refreshTable]);

  useDidMountEffect(() => {
    createRequest(1, true);
  }, [submitApplied]);

  useDidMountEffect(() => {
    createRequest(1, true, search);
  }, [search]);

  return (
    <>
      {componentManager === OpenComponent.HOME && (
        <div style={{ flex: 1, margin: 0 }}>
          <Title order={2} my="lg">
            User Management
          </Title>

          <Paper
            withBorder
            radius="md"
            shadow="lg"
            style={{
              flex: 1,
              height: '100%',
              padding: '20px 0',
              overflow: 'auto'
            }}
          >
            <Container fluid p="lg">
              {auth?.role === Roles.PARTNER && (
                <PartnerUserFilter
                  filterValues={filterValues}
                  setIsLoadResellers={setIsLoadResellers}
                  isLoadResellers={isLoadResellers}
                  filterSelectData={filterSelectData}
                  setSubmitApplied={setSubmitApplied}
                  isLoading={isLoading}
                />
              )}
              {auth?.role === Roles.RESELLER && (
                <ResellerUserFilter
                  filterValues={filterValues}
                  setIsLoadMerchants={setIsLoadMerchants}
                  isLoadMerchants={isLoadMerchants}
                  filterSelectData={filterSelectData}
                  setSubmitApplied={setSubmitApplied}
                  isLoading={isLoading}
                />
              )}
              {auth?.role === Roles.MERCHANT && (
                <MerchantUserManagementFilter
                  filterValues={filterValues}
                  filterSelectData={filterSelectData}
                  setSubmitApplied={setSubmitApplied}
                />
              )}
              {auth?.role === Roles.ADMINISTRATOR && (
                <UserManagementFilter
                  filterValues={filterValues}
                  setIsLoadPartners={setIsLoadPartners}
                  isLoadPartners={isLoadPartners}
                  filterSelectData={filterSelectData}
                  setSubmitApplied={setSubmitApplied}
                  isLoading={isLoading}
                />
              )}
            </Container>
            <Group p="md" position="apart">
              <SearchButton
                setSearch={setSearch}
                disabled={isLoading}
                search={search}
              />
              {paginationData && (
                <UpdateEnableDisableButtons
                  isChecked={isChecked}
                  userData={userData}
                  createRequest={createRequest}
                  setComponentManager={setComponentManager}
                  paginationData={paginationData}
                  firstUserId={firstUserId}
                />
              )}
            </Group>
            <Group p="md" position="right">
              {DynamicCreateButton(setComponentManager, isLoading, auth?.type)}
            </Group>
            <UserManagementTable
              userData={userData}
              isChecked={isChecked}
              setIsChecked={setIsChecked}
              setCheckedAll={setCheckedAll}
              checkedAll={checkedAll}
              paginationData={paginationData}
              createRequest={createRequest}
              setComponentManager={setComponentManager}
              setClickedUserId={setClickedUserId}
              firstUserId={firstUserId}
            />
          </Paper>
        </div>
      )}
      {componentManager === OpenComponent.CREATE_USER && (
        <AdminCreateUser
          filterValues={filterValues.values}
          setComponentManager={setComponentManager}
          setRefreshTable={setRefreshTable}
        />
      )}
      {componentManager === OpenComponent.UPDATE_USER && (
        <AdminUpdateUser
          setComponentManager={setComponentManager}
          selectedId={isChecked[0]}
          setRefreshTable={setRefreshTable}
        />
      )}
      {clickedUserId && componentManager === OpenComponent.VIEW_USER && (
        <AdminUserInformation
          selectedId={clickedUserId}
          setComponentManager={setComponentManager}
        />
      )}
    </>
  );
}

export default AdminUserManagement;
